import React, { useState } from "react";
import type { VisualIndicatorJobView } from "../../../../../hooks/api/settings/useVisualIndicatorJob";
import { estimateMinutes, formatCount } from "./visualIndicator";

const BOX: Record<"running" | "done", React.CSSProperties> = {
  running: { border: "1px solid #a9d1ff", background: "#eef6ff" },
  done: { border: "1px solid #9fd8b8", background: "#edf8f1" },
};

const boxStyle = (tone: "running" | "done"): React.CSSProperties => ({
  ...BOX[tone],
  borderRadius: 8,
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: tone === "running" ? 8 : 6,
});

const HEAD: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 };
const TITLE: React.CSSProperties = { fontSize: 14, fontWeight: 600 };
const BODY: React.CSSProperties = { fontSize: 13, color: "#44475a" };

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** "18 were left as they are: 11 had been renamed by someone since syncing, 5 are archived, …" */
export const skippedSentence = (skipped: VisualIndicatorJobView["status"]["skipped"], failed: number): string | null => {
  const { renamedByUser = 0, archived = 0, noAccess = 0 } = skipped ?? ({} as any);
  const parts: string[] = [];
  if (renamedByUser) parts.push(`${formatCount(renamedByUser)} had been renamed by someone since syncing`);
  if (archived) parts.push(`${formatCount(archived)} ${plural(archived, "is", "are")} archived`);
  if (noAccess) parts.push(`${formatCount(noAccess)} ${plural(noAccess, "is", "are")} on boards SIMB can't edit`);
  const left = renamedByUser + archived + noAccess;
  const sentences: string[] = [];
  if (left) sentences.push(`${formatCount(left)} ${plural(left, "was", "were")} left as ${plural(left, "it is", "they are")}: ${parts.join(", ")}.`);
  if (failed) sentences.push(`${formatCount(failed)} couldn't be renamed.`);
  return sentences.length ? sentences.join(" ") : null;
};

interface VisualIndicatorBannerProps {
  job: VisualIndicatorJobView;
  onStop: () => Promise<void> | void;
  onDismiss: () => void;
}

/** Mockup frames 4a and 4b: a bulk rename running, finished, or stopped. */
const VisualIndicatorBanner = ({ job, onStop, onDismiss }: VisualIndicatorBannerProps) => {
  const [stopping, setStopping] = useState(false);
  const { status, info } = job;
  const remove = info.mode === "remove";
  const total = Math.max(status.totalItems ?? 0, status.processedItems ?? 0);
  const processed = status.processedItems ?? 0;

  if (status.status === "running") {
    const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    const title = remove
      ? `Removing ${info.from ?? "the indicator"} from existing items`
      : info.from && info.to
        ? `Changing ${info.from} to ${info.to} on existing items`
        : `Changing existing items to ${info.to ?? "the new indicator"}`;
    const newSyncs = remove ? `New syncs don't get ${info.from ?? "it"}.` : info.to ? `New syncs already use ${info.to}.` : "";
    const stop = async () => {
      setStopping(true);
      try {
        await onStop();
      } finally {
        setStopping(false);
      }
    };
    return (
      <div role="status" style={boxStyle("running")} data-testid="visual-indicator-banner">
        <div style={HEAD}>
          <span style={TITLE}>{title}</span>
          <button
            type="button"
            disabled={stopping}
            onClick={stop}
            style={{
              height: 32,
              padding: "0 12px",
              borderRadius: 4,
              border: "1px solid #c3c6d4",
              background: "#ffffff",
              fontSize: 13,
              color: "#323338",
              cursor: "pointer",
            }}
          >
            Stop
          </button>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          style={{ height: 6, borderRadius: 3, background: "#d0e6ff", overflow: "hidden" }}
        >
          <div style={{ width: `${pct}%`, height: 6, background: "#0073ea" }} />
        </div>
        <span style={BODY}>
          {`${formatCount(processed)} of ${formatCount(total)} items · about ${estimateMinutes(Math.max(0, total - processed))} min left. ${newSyncs} You can close Settings; this keeps running.`.replace(
            /\s+/g,
            " ",
          )}
        </span>
      </div>
    );
  }

  const renamed = formatCount(status.renamed ?? 0);
  const title =
    status.status === "stopped"
      ? remove
        ? `Stopped. ${info.from ?? "The indicator"} was removed from ${renamed} items`
        : `Stopped. ${renamed} items were changed to ${info.to ?? "the new indicator"}`
      : remove
        ? `Removed ${info.from ?? "the indicator"} from ${renamed} items`
        : `${renamed} items now show ${info.to ?? "the new indicator"}`;
  const rest =
    status.status === "stopped"
      ? remove
        ? "The rest keep it."
        : `The rest keep ${info.from ?? "their current indicator"}. New syncs use ${info.to ?? "the new indicator"}.`
      : null;
  const skipped = skippedSentence(status.skipped, status.failed ?? 0);

  return (
    <div role="status" style={boxStyle("done")} data-testid="visual-indicator-banner">
      <div style={HEAD}>
        <span style={TITLE}>{title}</span>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          style={{ width: 32, height: 32, borderRadius: 4, border: 0, background: "transparent", color: "#323338", cursor: "pointer" }}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      {(rest || skipped) && <span style={BODY}>{[rest, skipped].filter(Boolean).join(" ")}</span>}
    </div>
  );
};

export default VisualIndicatorBanner;
